import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import { EXHIBITIONS } from '../lib/data';

const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'on view', label: 'Now on view' },
    { key: 'closing soon', label: 'Closing soon' },
    { key: 'archive', label: 'Archive' },
];

const ExhibitionsPage = () => {
    const [filter, setFilter] = useState('all');

    const current = useMemo(
        () => EXHIBITIONS.filter((e) => e.status === 'On view'),
        []
    );
    const filtered = useMemo(() => {
        if (filter === 'all') return EXHIBITIONS.filter((e) => e.status !== 'On view');
        return EXHIBITIONS.filter((e) => e.status.toLowerCase() === filter);
    }, [filter]);

    return (
        <div className="bg-bone text-charcoal min-h-screen" data-testid="exhibitions-page">
            <Header />

            {/* Page intro */}
            <section className="pt-36 md:pt-44 lg:pt-52 pb-12 md:pb-16 px-6 md:px-12 lg:px-16">
                <div className="grid grid-cols-12 gap-x-6 gap-y-10">
                    <Reveal delay={0.1} className="col-span-12">
                        <nav className="text-[11px] tracking-eyebrow uppercase text-charcoal/50 mb-6 flex items-center gap-2">
                            <Link to="/" className="hover:opacity-60 transition-opacity">Home</Link>
                            <span>/</span>
                            <span className="text-charcoal/80">Exhibitions</span>
                        </nav>
                        <h1
                            className="font-sans font-light text-charcoal leading-[0.9] tracking-normal text-6xl sm:text-7xl md:text-[9vw] lg:text-[8vw]"
                            data-testid="exhibitions-page-title"
                        >
                            Exhibitions
                        </h1>
                        <p className="mt-8 md:mt-10 max-w-2xl text-charcoal/75 text-base md:text-lg leading-relaxed font-light">
                            An archive of exhibitions. Solo presentations, group shows, and curatorial projects,
                            documented from December 2025.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Now on view */}
            {current.length > 0 && (
                <section className="px-6 md:px-12 lg:px-16 pb-20 md:pb-28">
                    <Reveal>
                        <div className="flex items-baseline justify-between border-b border-[#E6E4DD] pb-4 mb-10 md:mb-14">
                            <h2
                                className="font-sans font-light text-3xl md:text-4xl text-charcoal"
                            >
                                Now on view
                            </h2>
                            <span className="text-[11px] tracking-eyebrow uppercase text-charcoal/50">
                                {current.length} {current.length === 1 ? 'exhibition' : 'exhibitions'}
                            </span>
                        </div>
                    </Reveal>

                    {current.map((ex) => (
                        <CurrentBanner key={ex.id} ex={ex} />
                    ))}
                </section>
            )}

            {/* Past Exhibitions */}
            <section className="px-6 md:px-12 lg:px-16 pb-24 md:pb-40 border-t border-[#E6E4DD] pt-20 md:pt-28">
                <Reveal>
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
                        <h2
                            className="font-sans font-light text-3xl md:text-4xl text-charcoal"
                            data-testid="past-exhibitions-heading"
                        >
                            Past Exhibitions
                        </h2>
                        <ul
                            className="flex flex-wrap items-center gap-x-7 gap-y-2"
                            data-testid="exhibitions-filters"
                        >
                            {FILTERS.map((f) => (
                                <li key={f.key}>
                                    <button
                                        onClick={() => setFilter(f.key)}
                                        data-testid={`filter-${f.key.replace(/\s+/g, '-')}`}
                                        className={`text-[11px] tracking-eyebrow uppercase pb-1 border-b transition-colors duration-500 ${filter === f.key
                                                ? 'border-charcoal text-charcoal'
                                                : 'border-transparent text-charcoal/50 hover:text-charcoal'
                                            }`}
                                    >
                                        {f.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </Reveal>

                <ul className="space-y-16 md:space-y-24">
                    {filtered.map((ex, i) => (
                        <PastRow key={ex.id} ex={ex} idx={i} />
                    ))}
                    {filtered.length === 0 && (
                        <li className="text-charcoal/60 text-sm font-light">
                            No exhibitions match this filter.
                        </li>
                    )}
                </ul>
            </section>

            <Footer />
        </div>
    );
};

const CurrentBanner = ({ ex }) => (
    <Reveal>
        <Link
            to={`/exhibitions/${ex.id}`}
            className="group block"
            data-testid={`current-exhibition-${ex.id}`}
        >
            <div className={`relative w-full overflow-hidden bg-beige ${ex.imageFit === 'contain' ? '' : 'aspect-[16/8] md:aspect-[16/7]'}`} style={ex.imageFit === 'contain' ? { aspectRatio: ex.imageAspect || '41 / 18' } : undefined}>
                <motion.img
                    src={ex.image}
                    alt={ex.title}
                    initial={{ scale: ex.imageFit === 'contain' ? 1 : 1.06 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
                    className={`absolute inset-0 w-full h-full ${ex.imageFit === 'contain' ? 'object-contain' : 'object-cover transition-transform duration-[1600ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.03]'}`}
                    style={ex.imageFit === 'contain' ? undefined : { filter: 'grayscale(0.25)' }}
                />
                {ex.imageFit !== 'contain' && <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-black/0" />}
                <span className="absolute top-5 left-5 md:top-6 md:left-6 text-[10px] tracking-eyebrow uppercase text-bone bg-charcoal px-3 py-1.5">
                    {ex.status}
                </span>
            </div>
            <div className="mt-6 md:mt-8 grid grid-cols-12 gap-x-6 gap-y-3 items-end">
                <div className="col-span-12 md:col-span-8">
                    <p className="text-[11px] tracking-eyebrow uppercase text-charcoal/60">
                        {ex.dates} · {ex.location}
                    </p>
                    <h3
                        className="mt-3 font-sans font-light text-4xl md:text-5xl lg:text-6xl text-charcoal leading-[1] tracking-normal group-hover:opacity-75 transition-opacity duration-500"
                    >
                        {ex.title}
                    </h3>
                    {ex.artist && (
                        <p className="mt-2 font-sans font-light text-charcoal/70 text-xl md:text-2xl">
                            by {ex.artist}
                        </p>
                    )}
                </div>
                <div className="col-span-12 md:col-span-4 md:text-right">
                    <span className="editorial-link">
                        <span>View exhibition</span>
                        <span className="arrow">→</span>
                    </span>
                </div>
            </div>
        </Link>
    </Reveal>
);

const PastRow = ({ ex, idx }) => (
    <motion.li
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: (idx % 3) * 0.05 }}
        data-testid={`past-exhibition-${ex.id}`}
    >
        <Link to={`/exhibitions/${ex.id}`} className="group block">
            <div className="grid grid-cols-12 gap-x-6 gap-y-6 items-start">
                <div className="col-span-12 md:col-span-2">
                    <span className="text-[11px] tracking-eyebrow uppercase text-charcoal/60">
                        {ex.dateShort || ex.dates}
                    </span>
                    <div className="mt-3">
                        <span className="inline-block text-[10px] tracking-eyebrow uppercase px-2 py-1 border border-charcoal/25 text-charcoal/70">
                            {ex.status}
                        </span>
                    </div>
                </div>

                <div className="col-span-12 md:col-span-10 order-2 md:order-none">
                    <div className={`relative w-full overflow-hidden bg-beige ${ex.imageFit === 'contain' ? '' : 'aspect-[16/8] md:aspect-[16/6]'}`} style={ex.imageFit === 'contain' ? { aspectRatio: ex.imageAspect || '41 / 18' } : undefined}>
                        <img
                            src={ex.image}
                            alt={ex.title}
                            className={`absolute inset-0 w-full h-full ${ex.imageFit === 'contain' ? 'object-contain' : 'object-cover transition-transform duration-[1600ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.03]'}`}
                            style={ex.imageFit === 'contain' ? undefined : { filter: 'grayscale(0.35)' }}
                        />
                    </div>
                    <div className="mt-5 md:mt-7 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                        <div>
                            <h3
                                className="font-sans font-light text-3xl md:text-4xl lg:text-5xl text-charcoal leading-[1] tracking-normal group-hover:opacity-75 transition-opacity duration-500"
                            >
                                {ex.title}
                                {ex.artist && ex.artist !== 'Group exhibition' && (
                                    <span className="font-sans font-light text-charcoal/70"> · {ex.artist}</span>
                                )}
                            </h3>
                            <p className="mt-2 text-[11px] tracking-eyebrow uppercase text-charcoal/55">
                                {ex.location} · Curated by {ex.curator}
                            </p>
                        </div>
                        <span className="editorial-link self-start md:self-end">
                            <span>View</span>
                            <span className="arrow">→</span>
                        </span>
                    </div>
                </div>
            </div>
        </Link>
    </motion.li>
);

export default ExhibitionsPage;
